import React, { useState } from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { aboutMenus } from '../data/aboutMenus';

const Intro = styled.p`
  font-size: 1.05rem;
  color: #555;
  line-height: 1.9;
  margin-bottom: 45px;
  word-break: keep-all;
`;

const GroupTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.3rem;
  color: #1a2a40;
  font-weight: 700;
  margin: 0 0 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;

  span.count {
    font-size: 0.85rem;
    font-weight: 700;
    color: #223E7E;
    background: #eef2fa;
    border-radius: 20px;
    padding: 3px 12px;
  }
`;

const CertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 20px;
  margin-bottom: 55px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 14px;
  }
`;

const Card = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #223E7E;
    box-shadow: 0 8px 24px rgba(34, 62, 126, 0.1);
    transform: translateY(-4px);
  }
`;

const ImageBox = styled.div`
  aspect-ratio: 3 / 4;
  background: #f7f8fa;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

const CertImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #b8bcc4;

  .icon { font-size: 2.2rem; }
  small { font-size: 0.8rem; }
`;

const CardCaption = styled.div`
  padding: 14px 12px 16px;
  text-align: center;
`;

const CertName = styled.p`
  font-size: 0.95rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  line-height: 1.45;
  word-break: keep-all;
`;

const CertSub = styled.p`
  font-size: 0.8rem;
  color: #888;
  margin: 6px 0 0;
  line-height: 1.4;
  word-break: keep-all;
`;

const licenses = [
  { name: '실내건축공사업', img: '/images/license_interior.jpg' },
  { name: '조경식재공사업', img: '/images/license_landscape.jpg' },
  { name: '도장·습식·방수·석공사업', sub: '주력분야 : 습식·방수공사', img: '/images/license_finishing.jpg' },
  { name: '금속창호·지붕건축물조립공사업', sub: '주력분야 : 지붕판금·건축물조립공사', img: '/images/license_metal.jpg' },
  { name: '지반조성·포장공사업', sub: '주력분야 : 포장공사', img: '/images/license_pavement.jpg' },
  { name: '소독업', img: '/images/license_disinfection.jpg' },
  { name: '저수조청소업', img: '/images/license_tank.jpg' },
  { name: '위생관리용역업', img: '/images/license_sanitation.jpg' },
];

const certifications = [
  { name: '사회적기업 확인서', img: '/images/cert_social.jpg' },
  { name: '여성기업 확인서', img: '/images/cert_women.jpg' },
  { name: '중소기업 확인서', img: '/images/cert_sme.jpg' },
  { name: '직접생산확인증명서', sub: '(청소업)', img: '/images/cert_direct.jpg' },
  { name: 'ISO45001', sub: '안전보건경영시스템', img: '/images/cert_iso45001.jpg' },
  { name: '특허증', sub: '목화다래 및 목화씨앗 추출물을 함유하는 세정제 조성물 및 제조방법', img: '/images/cert_patent.jpg' },
];

const CertCard = ({ name, sub, img }) => {
  const [error, setError] = useState(false);

  return (
    <Card>
      <ImageBox>
        {error ? (
          <Placeholder>
            <span className="icon">📄</span>
            <small>이미지 준비중</small>
          </Placeholder>
        ) : (
          <CertImage src={img} alt={name} onError={() => setError(true)} />
        )}
      </ImageBox>
      <CardCaption>
        <CertName>{name}</CertName>
        {sub && <CertSub>{sub}</CertSub>}
      </CardCaption>
    </Card>
  );
};

const Certifications = () => {
  return (
    <SubLayout
      title="보유 면허 및 인증현황"
      sidebarMenus={aboutMenus}
      bannerBg="/images/bg_building.jpg"
    >
      <Intro>
        주식회사 두성시스템은 전문건설업 면허부터 물탱크청소, 소독업 등 각종 업종을 보유한 전문건설·종합관리 기업입니다.
        신뢰할 수 있는 시공과 서비스를 위해 관련 면허 및 인증을 성실히 취득·유지하고 있습니다.
      </Intro>

      <GroupTitle>
        면허 <span className="count">{licenses.length}</span>
      </GroupTitle>
      <CertGrid>
        {licenses.map((item) => (
          <CertCard key={item.name} {...item} />
        ))}
      </CertGrid>

      <GroupTitle>
        인증 <span className="count">{certifications.length}</span>
      </GroupTitle>
      <CertGrid>
        {certifications.map((item) => (
          <CertCard key={item.name} {...item} />
        ))}
      </CertGrid>
    </SubLayout>
  );
};

export default Certifications;
